import { useId, useMemo, useState } from 'react'
import { copyToClipboard } from '@services/clipboard'

/**
 * 24 単語 Recovery Mnemonic 表示 (一回限り)。register / rotate 両方で使用。
 *
 * - mnemonic を 4 列グリッドで表示 (印刷時 2 列 fallback)
 * - copy button で clipboard へ書き込み (`@services/clipboard` 経由で 90s 自動クリア保証、
 *   CLAUDE.md §2 ルール 7 厳守)
 * - 「保管しました」確認チェック必須 → enable で proceed button が活性化
 * - i18n agnostic: caller が表示文字列を全て props で渡す (`t()` 適用済の string)
 */
export interface RecoveryDisplayProps {
  mnemonic: string
  /** 確認チェック後に呼ばれる (caller が #login 遷移 / inline close 等を行う) */
  onProceed: () => void
  /** h2 のタイトル文字列 */
  title: string
  /** 警告ブロックの本文 */
  warning: string
  /** 通常時の copy button ラベル */
  copyLabel: string
  /** copy 成功直後 (2 秒間) の button ラベル */
  copiedLabel: string
  /** 確認チェック隣のラベル */
  confirmLabel: string
  /** 確認後の proceed button ラベル */
  proceedLabel: string
  /** 任意。Recovery 表示の下に置く補足 (例: register 時のメール認証案内)。 */
  footnote?: string
}

export const RecoveryDisplay = ({
  mnemonic,
  onProceed,
  title,
  warning,
  copyLabel,
  copiedLabel,
  confirmLabel,
  proceedLabel,
  footnote,
}: RecoveryDisplayProps): JSX.Element => {
  const [confirmed, setConfirmed] = useState(false)
  const [copied, setCopied] = useState(false)
  const confirmId = useId()
  const words = useMemo(() => mnemonic.split(/\s+/u), [mnemonic])

  const onCopy = async (): Promise<void> => {
    try {
      await copyToClipboard(mnemonic)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API が拒否された場合 (権限なし等) は無視 — ユーザーは手書きでもメモ可
    }
  }

  return (
    <div className="recovery-display" role="region" aria-live="polite">
      <h2>{title}</h2>
      <p className="recovery-display-warning">{warning}</p>

      <ol className="recovery-words">
        {words.map((w, i) => (
          <li key={`${i}-${w}`}>
            <span className="recovery-word-index">{i + 1}</span>
            <span className="recovery-word-text">{w}</span>
          </li>
        ))}
      </ol>

      <div className="recovery-actions">
        <button type="button" className="btn btn-secondary" onClick={() => void onCopy()}>
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>

      <label className="check-row" htmlFor={confirmId}>
        <input
          id={confirmId}
          type="checkbox"
          checked={confirmed}
          onChange={ev => setConfirmed(ev.target.checked)}
        />
        <span>{confirmLabel}</span>
      </label>

      <button
        type="button"
        className="btn btn-primary btn-lg btn-block"
        disabled={!confirmed}
        onClick={onProceed}
      >
        {proceedLabel}
      </button>

      {footnote && <p className="recovery-display-footnote">{footnote}</p>}
    </div>
  )
}
